import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  XMarkIcon, 
  DocumentTextIcon, 
  PrinterIcon,
  CalendarIcon,
  UserIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';
import { payslipsService } from '../../../services/accounting/payslips';
import { formatCurrency, formatDate } from '../../../utils/formatting';
import LoadingSpinner from '../../ui/LoadingSpinner';
import toast from 'react-hot-toast';

/**
 * PayslipModal Component
 * 
 * Modal for displaying employee payslips
 */
const PayslipModal = ({ isOpen, onClose, employee, period }) => {
  const { t } = useTranslation();
  const { currentCompany } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [payslip, setPayslip] = useState(null);

  // Load payslip when modal opens
  useEffect(() => {
    if (isOpen && employee && period && currentCompany?.id) {
      loadPayslip();
    }
  }, [isOpen, employee, period, currentCompany?.id]);

  const loadPayslip = async () => {
    try {
      setLoading(true);
      
      // First try to get existing payslips
      let payslips = [];
      try {
        payslips = await payslipsService.getEmployeePayslips(currentCompany.id, employee.id);
      } catch (payslipError) {
        console.warn('Could not load existing payslips:', payslipError);
        payslips = [];
      }
      
      const periodPayslip = payslips.find(p => p.periodId === period.id);
      
      if (periodPayslip) {
        setPayslip(periodPayslip);
      } else {
        // Generate payslip if it doesn't exist
        try {
          const { payrollService } = await import('../../../services/accounting/payroll');
          const payrollEntries = await payrollService.getPayrollEntries(currentCompany.id, period.id);
          const employeeEntry = payrollEntries.find(entry => entry.employeeId === employee.id);
          
          if (employeeEntry) {
            const generatedPayslip = await payslipsService.generatePayslip(
              currentCompany.id,
              period.id,
              employee.id,
              employeeEntry,
              'system'
            );
            setPayslip(generatedPayslip);
          } else {
            // Create a basic payslip from employee data if no payroll entry exists
            const basicPayslip = {
              id: `temp-${employee.id}-${period.id}`,
              companyId: currentCompany.id,
              periodId: period.id,
              employeeId: employee.id,
              employeeName: employee.name,
              employeeCode: employee.employeeId || employee.driverCode || `EMP${employee.id.slice(-4)}`,
              employeeRole: employee.role || 'Employee',
              employeeType: employee.type || 'employee',
              periodYear: period.year,
              periodMonth: period.month,
              periodStartDate: period.startDate,
              periodEndDate: period.endDate,
              basicSalary: employee.salary || 0,
              commission: 0,
              bonuses: 0,
              deductions: 0,
              totalEarnings: employee.salary || 0,
              totalDeductions: 0,
              netPay: employee.salary || 0,
              companyName: currentCompany.name || 'Company',
              companyAddress: currentCompany.address || '',
              status: 'draft',
              paymentStatus: 'draft',
              generatedAt: new Date(),
              isTemporary: true
            };
            setPayslip(basicPayslip);
          }
        } catch (entryError) {
          console.error('Error loading payroll entries:', entryError);
          toast.error(t('payroll.error.no_payroll_entry', 'No payroll entry found for this employee'));
        }
      }
    } catch (error) {
      console.error('Error loading payslip:', error);
      toast.error(t('payroll.error.loading_payslip', 'Failed to load payslip'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    setPayslip(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DocumentTextIcon className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('payroll.payslip', 'Payslip')}
              </h2>
              <p className="text-sm text-gray-500">
                {employee?.name} - {period?.year}-{String(period?.month).padStart(2, '0')}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="btn btn-secondary btn-sm"
              disabled={loading || !payslip}
            >
              <PrinterIcon className="w-4 h-4 mr-2" />
              {t('common.print', 'Print')}
            </button>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
              <span className="ml-3">{t('common.loading', t('common.loading'))}</span>
            </div>
          ) : payslip ? (
            <div className="p-8 print:p-4" id="payslip-content">
              {/* Company Header */}
              <div className="text-center mb-8 print:mb-4">
                <div className="flex items-center justify-center mb-2">
                  <BuildingOfficeIcon className="w-8 h-8 text-blue-600 mr-3" />
                  <h1 className="text-2xl font-bold text-gray-900">
                    {payslip.companyName}
                  </h1>
                </div>
                {payslip.companyAddress && (
                  <p className="text-gray-600">{payslip.companyAddress}</p>
                )}
                <h2 className="text-xl font-semibold text-blue-600 mt-4">
                  {t('payroll.payslip', 'PAYSLIP')}
                </h2>
              </div>

              {/* Employee and Period Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <UserIcon className="w-4 h-4 mr-2" />
                    {t('payroll.employee_details', 'Employee Details')}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.employee_name', t('common.name'))}:</span>
                      <span className="font-medium">{payslip.employeeName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.employee_id', 'Employee ID')}:</span>
                      <span className="font-medium">{payslip.employeeCode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.role', 'Role')}:</span>
                      <span className="font-medium">{payslip.employeeRole}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.type', 'Type')}:</span>
                      <span className="font-medium capitalize">{payslip.employeeType}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {t('payroll.period_details', 'Period Details')}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.period', 'Period')}:</span>
                      <span className="font-medium">
                        {payslip.periodYear}-{String(payslip.periodMonth).padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.start_date', 'Start Date')}:</span>
                      <span className="font-medium">
                        {formatDate(payslip.periodStartDate)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.end_date', 'End Date')}:</span>
                      <span className="font-medium">
                        {formatDate(payslip.periodEndDate)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('payroll.payment_status', t('common.status'))}:</span>
                      <span className={`font-medium ${
                        payslip.paymentStatus === 'paid' ? 'text-green-600' : 'text-amber-600'
                      }`}>
                        {t(`payroll.status.${payslip.paymentStatus}`, payslip.paymentStatus)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Earnings and Deductions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Earnings */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CurrencyDollarIcon className="w-5 h-5 mr-2 text-green-600" />
                    {t('payroll.earnings', 'Earnings')}
                  </h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-700">{t('payroll.basic_salary', 'Basic Salary')}:</span>
                        <span className="font-medium text-green-700">
                          {formatCurrency(payslip.basicSalary)}
                        </span>
                      </div>
                      {payslip.commission > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">{t('payroll.commission', 'Commission')}:</span>
                          <span className="font-medium text-green-700">
                            {formatCurrency(payslip.commission)}
                          </span>
                        </div>
                      )}
                      {payslip.bonuses > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-700">{t('payroll.bonuses', 'Bonuses')}:</span>
                          <span className="font-medium text-green-700">
                            {formatCurrency(payslip.bonuses)}
                          </span>
                        </div>
                      )}
                      <div className="border-t border-green-300 pt-3">
                        <div className="flex justify-between font-semibold">
                          <span className="text-gray-900">{t('payroll.total_earnings', 'Total Earnings')}:</span>
                          <span className="text-green-700">
                            {formatCurrency(payslip.totalEarnings)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t('payroll.deductions', 'Deductions')}
                  </h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="space-y-3">
                      {payslip.totalDeductions > 0 ? (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-700">{t('payroll.total_deductions', 'Total Deductions')}:</span>
                            <span className="font-medium text-red-700">
                              {formatCurrency(payslip.totalDeductions)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="text-center text-gray-500 py-4">
                          {t('payroll.no_deductions', 'No deductions')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-blue-900">
                    {t('payroll.net_pay', 'Net Pay')}
                  </h3>
                  <div className="text-3xl font-bold text-blue-900">
                    {formatCurrency(payslip.netPay)}
                  </div>
                </div>
                <p className="text-sm text-blue-700 mt-2">
                  {t('payroll.net_pay_description', 'Amount to be paid after all deductions')}
                </p>
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-gray-500 border-t border-gray-200 pt-4">
                <p>
                  {t('payroll.generated_on', 'Generated on')}: {' '}
                  {formatDate(payslip.generatedAt?.toDate?.() || payslip.generatedAt)}
                </p>
                <p className="mt-1">
                  {t('payroll.payslip_footer', 'This is a computer-generated payslip and does not require a signature.')}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">{t('payroll.no_payslip', 'No payslip available')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayslipModal;